import React, { useEffect, useState, useRef } from "react";

import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import { EpisodesIcon } from "../common/icons";
import SidebarView, { SidebarRef } from "../common/Sidebar";
import { episodeService } from "./services";
// import { videosService } from "../Videos/services";
// import type { VideosResponse } from "../Videos/types";
import ShareEpisodeDialog from "./components/ShareEpisodeDialog";
import { WebSocketEventName } from "../common/enums/WebSocketEventName";
import { useWebSocket } from "../../hooks/useWebSocket";
import Insights from "./components/Insights";
import Preview from "./components/Preview";
import Breadcrumbs from "../common/Breadcrumbs";
import EpisodeSkeletonLoader from "./components/EpisodeSkeletonLoader";
import "../../episode-mobile.css";
import Headerbar from "../common/Headerbar";
import { videosService } from "../Videos/services";
import { VideoCard as VideosPageVideoCard } from "../Videos/parts/VideoCard";
import { audiogramsService } from "../Audiograms/services";
import AudiogramCard from "../Audiograms/components/AudiogramCard";

export default function Episode() {
  const { id: episodeId } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const sidebarRef = useRef<SidebarRef>(null);
  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const [showShareEpisodeModal, setShowShareEpisodeModal] = useState(false);

  type PanelFocus = "split" | "left" | "right";
  const [panelFocus, setPanelFocus] = useState<PanelFocus>("split");

  const toggleLeftPanel = () => {
    setPanelFocus((prev) => {
      if (prev === "left") return "split";
      if (prev === "right") return "split";
      return "left";
    });
  };

  const toggleRightPanel = () => {
    setPanelFocus((prev) => {
      if (prev === "right") return "split";
      if (prev === "left") return "split";
      return "right";
    });
  };

  const getLeftPanelWidth = () => {
    if (isMobile) return "100%";
    if (panelFocus === "left") return "100%";
    return "56%";
  };

  const getRightPanelWidth = () => {
    if (isMobile) return "100%";
    if (panelFocus === "right") return "100%";
    return "44%";
  };

  const getLeftToggleTooltip = () =>
    panelFocus === "left" ? "Back to split view" : "Expand Content Hub";

  const getRightToggleTooltip = () =>
    panelFocus === "right" ? "Back to split view" : "Expand Preview";

  const socket = useWebSocket();

  const { data: insights, refetch } = useQuery({
    queryKey: ["insights", episodeId],
    queryFn: () => episodeService.fetchInsights(episodeId),
  });

  const { data: episode, isLoading: isEpisodeLoading } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
  });

  const { data: videosData, isLoading: isVideosLoading } = useQuery({
    queryKey: ["getVideos", episodeId],
    queryFn: () => videosService.getVideos(episodeId),
  });

  const isAudioEpisode = episode?.upload?.mime_type?.startsWith("audio/");

  const { data: audiogramsData, isLoading: isAudiogramsLoading } = useQuery({
    queryKey: ["getAudiograms", episodeId],
    queryFn: () => audiogramsService.getAudiograms(episodeId),
    enabled: !!episodeId && !!isAudioEpisode,
  });

  // const { data: videosData } = useQuery<VideosResponse | undefined>({
  //   queryKey: ["getVideos", episodeId],
  //   queryFn: () => videosService.getVideos(episodeId),
  // });

  const handleOpenShareEpisodeModal = () => {
    setShowShareEpisodeModal(true);
  };

  const handleCloseShareEpisodeModal = () => {
    setShowShareEpisodeModal(false);
  };

  useEffect(() => {
    if (socket) {
      socket.on(WebSocketEventName.EPISODE_INSIGHTS_GENERATED, async () => {
        await refetch();
      });
    }

    return () => {
      if (socket) {
        socket.off(WebSocketEventName.EPISODE_INSIGHTS_GENERATED);
      }
    };
  }, [socket]);

  // Prevent page scrolling
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalStyle;
      document.documentElement.style.overflow = originalStyle;
    };
  }, []);

  const cardMarginX = isMobile ? 0.5 : 2.5;
  const shortVideos = videosData?.videos || [];
  const shortVideosPreview = shortVideos.slice(0, 3);

  const audiograms = [...(audiogramsData?.audiograms || [])].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const audiogramsPreview = audiograms.slice(0, 3);

  const openVideosPage = () => {
    if (episodeId) {
      window.location.assign(`/episodes/${episodeId}/videos`);
    }
  };

  const renderShortVideosContent = () => {
    return (
      <Grid
        container
        spacing={1}
        sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}
      >
        <VideosPageVideoCard
          isLoading={isVideosLoading}
          setModalOpen={openVideosPage}
          data={{ videos: shortVideosPreview }}
          episodeId={episodeId || ""}
          compact
        />
      </Grid>
    );
  };

  const renderAudiogramsContent = () => {
    if (!isAudiogramsLoading && audiogramsPreview.length === 0) {
      return (
        <Typography sx={{ fontSize: "12px", color: "text.secondary", py: 1.5 }}>
          No audiograms yet. Generate one to see it here.
        </Typography>
      );
    }
    return (
      <Grid
        container
        spacing={1}
        sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}
      >
        {audiogramsPreview.map((audiogram) => (
          <AudiogramCard
            key={audiogram.id}
            audiogram={audiogram}
            episodeId={episodeId || ""}
            compact
          />
        ))}
      </Grid>
    );
  };

  // Check if episode is audio file type
  const isAudioFile = episode?.upload?.mime_type?.startsWith("audio/");
  return (
    <>
      {showShareEpisodeModal && episode && (
        <ShareEpisodeDialog
          episode={episode}
          handleClose={handleCloseShareEpisodeModal}
        />
      )}
      <SidebarView
        ref={sidebarRef}
        pageName={`Episode - ${episode?.name || ""}`}
      />
      <Box
        component="main"
        className="episode-main-content"
        sx={{
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
          [theme.breakpoints.down("sm")]: {
            marginLeft: 0,
          },
        }}
      >
        <Headerbar
          pageName={`Episode - ${episode?.name || ""}`}
          onMobileMenuClick={handleMobileMenuToggle}
          headerLeftContent={
            <Breadcrumbs
              items={[
                {
                  label: "Episodes",
                  path: "/episodes",
                  icon: <EpisodesIcon sx={{ fontSize: 18 }} />,
                },
                {
                  label: episode?.name || "Episode",
                  path: `/episodes/${episodeId}`,
                },
              ]}
              showHome={false}
              inHeader
            />
          }
        />

        {isEpisodeLoading && <EpisodeSkeletonLoader />}

        {!isEpisodeLoading && (
          <Box
            className="episode-content"
            sx={{
              flexGrow: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              mx: cardMarginX,
              mt: 0.75,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: "hidden",
                px: isMobile ? 1 : 1.2,
                pt: 0.2,
                pb: 1.3,
                backgroundColor: "transparent",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 0,
                  height: "100%",
                  position: "relative",
                }}
              >
                {panelFocus !== "right" && (
                  <Box
                    sx={{
                      width: getLeftPanelWidth(),
                      minWidth: 0,
                      height: isMobile ? "52%" : "100%",
                      overflow: "hidden",
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      p: isMobile ? 1 : 1.2,
                      transition: "width 0.25s ease",
                      [theme.breakpoints.down("sm")]: {
                        px: 0,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        height: "100%",
                        minHeight: 0,
                      }}
                    >
                      {!isAudioFile && (
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "primary.light",
                            borderRadius: 2,
                            px: 1.5,
                            py: 1.25,
                            backgroundColor: "rgba(37, 99, 235, 0.05)",
                            boxShadow: "0 2px 8px rgba(37, 99, 235, 0.08)",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                color: "primary.main",
                              }}
                            >
                              Short videos ({shortVideos.length})
                            </Typography>
                            <Button
                              size="small"
                              href={`/episodes/${episodeId}/videos`}
                              sx={{
                                minWidth: "auto",
                                px: 1.1,
                                py: 0.35,
                                fontSize: "11px",
                                fontWeight: 600,
                                textTransform: "none",
                                color: "primary.main",
                                border: "1px solid",
                                borderColor: "primary.light",
                                borderRadius: "999px",
                                backgroundColor: "rgba(37, 99, 235, 0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(37, 99, 235, 0.18)",
                                  borderColor: "primary.main",
                                },
                              }}
                            >
                              View all
                            </Button>
                          </Box>

                          {renderShortVideosContent()}
                        </Box>
                      )}

                      {isAudioFile && (
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "primary.light",
                            borderRadius: 2,
                            px: 1.5,
                            py: 1.25,
                            backgroundColor: "rgba(37, 99, 235, 0.05)",
                            boxShadow: "0 2px 8px rgba(37, 99, 235, 0.08)",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                color: "primary.main",
                              }}
                            >
                              Audiograms ({audiograms.length})
                            </Typography>
                            <Button
                              size="small"
                              href={`/episodes/${episodeId}/audiograms`}
                              sx={{
                                minWidth: "auto",
                                px: 1.1,
                                py: 0.35,
                                fontSize: "11px",
                                fontWeight: 600,
                                textTransform: "none",
                                color: "primary.main",
                                border: "1px solid",
                                borderColor: "primary.light",
                                borderRadius: "999px",
                                backgroundColor: "rgba(37, 99, 235, 0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(37, 99, 235, 0.18)",
                                  borderColor: "primary.main",
                                },
                              }}
                            >
                              View all
                            </Button>
                          </Box>

                          {renderAudiogramsContent()}
                        </Box>
                      )}

                      <Box
                        sx={{
                          flexGrow: 1,
                          minHeight: 0,
                          borderTop: "1px dashed",
                          borderColor: "divider",
                          pt: 1,
                        }}
                      >
                        {episodeId && (
                          <Insights
                            insights={insights}
                            refetch={refetch}
                            episodeId={episodeId}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {!isMobile && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      position: "relative",
                      width: 36,
                      px: 0.25,
                      flexShrink: 0,
                      gap: 1,
                      mt: "150px",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        borderLeft: "1px solid",
                        borderColor: "divider",
                      },
                    }}
                  >
                    <Tooltip title={getLeftToggleTooltip()} placement="top">
                      <IconButton
                        size="small"
                        onClick={toggleLeftPanel}
                        sx={{
                          width: 26,
                          height: 54,
                          borderRadius: "8px",
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "background.paper",
                          color: "text.secondary",
                          zIndex: 1,
                          "&:hover": {
                            backgroundColor: "action.hover",
                            color: "primary.main",
                            borderColor: "primary.light",
                          },
                          transition: "all 0.15s ease",
                        }}
                      >
                        {panelFocus === "left" ? (
                          <ChevronLeftIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ChevronRightIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={getRightToggleTooltip()} placement="top">
                      <IconButton
                        size="small"
                        onClick={toggleRightPanel}
                        sx={{
                          width: 26,
                          height: 54,
                          borderRadius: "8px",
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "background.paper",
                          color: "text.secondary",
                          zIndex: 1,
                          "&:hover": {
                            backgroundColor: "action.hover",
                            color: "primary.main",
                            borderColor: "primary.light",
                          },
                          transition: "all 0.15s ease",
                        }}
                      >
                        {panelFocus === "right" ? (
                          <ChevronRightIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ChevronLeftIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {panelFocus !== "left" && (
                  <Box
                    sx={{
                      width: getRightPanelWidth(),
                      minWidth: 0,
                      height: isMobile ? "48%" : "100%",
                      overflow: "hidden",
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      p: isMobile ? 1 : 1.2,
                      transition: "width 0.25s ease",
                      [theme.breakpoints.down("sm")]: {
                        px: 0,
                      },
                    }}
                  >
                    {episodeId && (
                      <Preview
                        episode={episode}
                        episodeId={episodeId}
                        isAudioFile={isAudioFile}
                        onShareClick={handleOpenShareEpisodeModal}
                        isExpandedView={panelFocus === "right"}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}
